// @ts-check

/**
 * Pago que informa el residente desde Gastos comunes ("Ya pagué" → queda
 * pendiente de verificación). Arma el update de `payments` que aceptan las
 * reglas 0D (residentePagaOk):
 *
 * - Nunca lleva buildingid (contrato C9): el edificio del pago lo fijó el
 *   cierre, y una variante como '010' lo sacaba de todas las consultas del
 *   staff (cobranza, mora, PDF).
 * - Montos consistentes: paidAmount = lo ya pagado + lo informado y
 *   remainingAmount = amount − paidAmount (las reglas toleran ±0,02).
 * - Tope: el residente informa hasta lo que debe en ese pago. Un sobrepago lo
 *   registra la administración al verificar (Cobranza → Aprobar con el monto
 *   recibido): si lo declarara el residente, el cierre lo tomaría como saldo a
 *   favor sin verificación.
 *
 * paymentDate no va acá: el llamador lo pone con serverTimestamp() (las reglas
 * exigen la hora del servidor).
 */

const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

/**
 * Lo que falta pagar de un pago según las reglas (amount − paidAmount), nunca
 * negativo.
 *
 * @param {{ amount?: unknown, paidAmount?: unknown } | null | undefined} pago
 */
export function saldoPendienteDePago(pago) {
  return Math.max(0, r2((Number(pago?.amount) || 0) - (Number(pago?.paidAmount) || 0)));
}

/**
 * true si el monto informado supera lo que se debe en el pago (con un centavo
 * de margen, dentro de la tolerancia de las reglas).
 *
 * @param {{ amount?: unknown, paidAmount?: unknown } | null | undefined} pago
 * @param {number} monto - Monto que informa el residente
 */
export function excedeLoAdeudado(pago, monto) {
  return r2(monto) > saldoPendienteDePago(pago) + 0.01;
}

/**
 * Campos del update del pago informado (sin paymentDate).
 *
 * @param {object} args
 * @param {{ amount?: unknown, paidAmount?: unknown }} args.pago - Datos actuales del pago (leídos en la transacción)
 * @param {number} args.monto - Monto que informa el residente (> 0 y sin exceder lo adeudado)
 * @param {string} args.metodo - Medio de pago elegido
 * @param {string | number} args.paidBy - userid del residente (o uid de la sesión)
 * @param {string} args.paidByEmail - Email del token de la sesión
 * @param {string | null} [args.comprobanteUrl] - URL de Storage del comprobante, si subió uno
 * @returns {Record<string, any>}
 */
export function datosPagoInformado({ pago, monto, metodo, paidBy, paidByEmail, comprobanteUrl = null }) {
  const informado = r2(monto);
  const paidAmount = r2((Number(pago?.paidAmount) || 0) + informado);
  const datos = {
    status: "pendiente_verificacion",
    paidAmount,
    remainingAmount: r2((Number(pago?.amount) || 0) - paidAmount),
    lastPaymentAttempt: informado,
    paymentMethod: metodo,
    paidBy,
    paidByEmail,
  };
  if (comprobanteUrl) datos.comprobanteUrl = comprobanteUrl;
  return datos;
}
